import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ProductsActions } from './products.actions';
import { catchError, map, mergeMap, of, tap } from 'rxjs';
import { ToastService } from '../../core/services/toast/toast.service';
import { ProductService } from '../../core/services/api/product/product.service';

@Injectable()
export class ProductsEffects {
  private actions$ = inject(Actions);
  private service = inject(ProductService);
  private toast = inject(ToastService);

  // === FETCH ALL ===
  fetchAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductsActions.fetchAll),
      mergeMap(() =>
        this.service.getAll().pipe(
          map((response) => ProductsActions.fetchAllSuccess({ items: response.data })),
          catchError((error) => of(ProductsActions.fetchAllFailure({ error })))
        )
      )
    )
  );

  // === FETCH PAGINATED ===
  fetchPaginated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductsActions.fetchPaginated),
       mergeMap(({ page, limit, search, sort, dateFrom, dateTo }) =>
        this.service.getPaginated({
          page,
          limit,
          search,
          sort,
          dateFrom,
          dateTo,
        }).pipe(
          map((response) =>
            ProductsActions.fetchPaginatedSuccess({
              items: response.data.list,
              pagination: response.data.pagination,
            })
          ),
          catchError((error) => of(ProductsActions.fetchPaginatedFailure({ error })))
        )
      )
    )
  );

  // === TOGGLE STATUS ===
  toggleStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductsActions.toggleStatus),
      mergeMap(({ id }) =>
        this.service.changeStatus(id, { isActive: true }).pipe(
          map((response) => ProductsActions.toggleStatusSuccess({ product: response.data })),
          catchError((error) => of(ProductsActions.toggleStatusFailure({ error })))
        )
      )
    )
  );

  // === CREATE ===
  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductsActions.create),
      mergeMap(({ product }) =>
        this.service.create(product).pipe(
          map((response) => ProductsActions.createSuccess({ product: response.data })),
          catchError((error) => of(ProductsActions.createFailure({ error })))
        )
      )
    )
  );

  // === TOASTS ===
  showSuccessToasts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProductsActions.createSuccess,
          ProductsActions.updateSuccess,
          ProductsActions.toggleStatusSuccess
        ),
        tap((action) => {
          if ('product' in action) {
            const p = action.product;

            switch (action.type) {
              case ProductsActions.toggleStatusSuccess.type: {
                const stateText = p.isActive ? 'activé' : 'désactivé';
                this.toast.success(`Produit "${p.name}" ${stateText} avec succès`);
                break;
              }

              case ProductsActions.createSuccess.type: {
                this.toast.success(`Produit "${p.name}" créé avec succès`);
                break;
              }

              case ProductsActions.updateSuccess.type: {
                this.toast.success(`Produit "${p.name}" mis à jour avec succès`);
                break;
              }
            }
          }
        })
      ),
    { dispatch: false }
  );

  showErrorToasts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProductsActions.createFailure,
          ProductsActions.updateFailure,
          ProductsActions.toggleStatusFailure
        ),
        tap(({ error }) => {
          const msg = error.message || 'Une erreur est survenue';
          this.toast.error(msg);
        })
      ),
    { dispatch: false }
  );
}
